import React from "react";
import { motion } from "framer-motion";
import { Users, CheckCircle, CursorClick, TrendUp, CurrencyDollar, Funnel } from "@phosphor-icons/react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const fmt = (n) => (n ?? 0).toLocaleString();
const fmtDay = (d) => { const [, m, day] = (d || "").split("-"); return m ? `${m}/${day}` : d; };

function Kpi({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5" data-testid={`admin-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${color}1a`, color }}><Icon size={18} weight="duotone" /></span>
      <p className="mt-3 font-heading text-3xl font-semibold text-slate-100">{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

export default function AdminOverview({ data }) {
  const k = data.kpis;
  const rev = data.revenue;
  const maxFunnel = Math.max(...data.funnel.map((f) => f.value), 1);

  return (
    <div data-testid="admin-overview">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Command Center</p>
      <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50">Platform overview</h1>
      <p className="mt-2 text-sm text-slate-400">Live metrics across acquisition, engagement and consult pipeline.</p>

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={Users} label="Total Users" value={fmt(k.total_users)} sub={`+${k.new_today} today · +${k.new_7d} this week`} color="#38BDF8" />
        <Kpi icon={CheckCircle} label="Assessments Done" value={fmt(k.total_completions)} sub={`${k.completion_rate}% completion rate`} color="#14B8A6" />
        <Kpi icon={CursorClick} label="Consult Clicks" value={fmt(k.cta_clicks)} sub={`${k.cta_rate}% of users`} color="#FBBF24" />
        <Kpi icon={TrendUp} label="New (30d)" value={fmt(k.new_30d)} sub={`${fmt(k.landing_views)} landing views`} color="#A78BFA" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Timeseries */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid="admin-timeseries">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-base font-medium text-slate-100">Growth — last 30 days</h3>
            <div className="flex gap-4 text-xs">
              <span className="flex items-center gap-1.5 text-slate-400"><span className="h-2 w-2 rounded-full bg-teal-400" /> Completions</span>
              <span className="flex items-center gap-1.5 text-slate-400"><span className="h-2 w-2 rounded-full bg-sky-400" /> Signups</span>
            </div>
          </div>
          <div className="mt-5 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.timeseries} margin={{ left: -20, right: 6, top: 6 }}>
                <defs>
                  <linearGradient id="gComp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#14B8A6" stopOpacity={0.4} /><stop offset="100%" stopColor="#14B8A6" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gSign" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#38BDF8" stopOpacity={0.35} /><stop offset="100%" stopColor="#38BDF8" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "#e2e8f0" }} labelFormatter={fmtDay} />
                <Area type="monotone" dataKey="completions" stroke="#14B8A6" strokeWidth={2} fill="url(#gComp)" />
                <Area type="monotone" dataKey="signups" stroke="#38BDF8" strokeWidth={2} fill="url(#gSign)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue / pipeline */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-slate-900 to-slate-950 p-6" data-testid="admin-revenue">
          <div className="flex items-center gap-2"><CurrencyDollar size={16} weight="fill" className="text-amber-400" /><p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Consult Pipeline</p></div>
          <p className="mt-4 font-heading text-4xl font-semibold text-amber-300">${fmt(rev.pipeline_value)}</p>
          <p className="text-xs text-slate-500">Attributed pipeline · {fmt(rev.cta_clicks)} clicks × ${rev.consult_price}</p>
          <div className="mt-6 space-y-3 border-t border-slate-800 pt-4 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Consult price</span><span className="text-slate-200">${rev.consult_price}/hr</span></div>
            <div className="flex justify-between"><span className="text-slate-400">CTA clicks (7d)</span><span className="text-slate-200">{fmt(rev.cta_clicks_7d)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">CTA clicks (total)</span><span className="text-slate-200">{fmt(rev.cta_clicks)}</span></div>
          </div>
          <p className="mt-4 text-[11px] leading-relaxed text-slate-600">Bookings are completed on Calendly. Pipeline reflects intent (CTA clicks), not confirmed revenue.</p>
        </motion.div>
      </div>

      {/* Funnel */}
      <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid="admin-funnel">
        <div className="flex items-center gap-2"><Funnel size={16} weight="duotone" className="text-teal-400" /><h3 className="font-heading text-base font-medium text-slate-100">Conversion funnel</h3></div>
        <div className="mt-5 space-y-3">
          {data.funnel.map((f, i) => {
            const pct = Math.round((f.value / maxFunnel) * 100);
            const prev = i > 0 ? data.funnel[i - 1].value : null;
            const drop = prev ? Math.round((f.value / prev) * 100) : null;
            return (
              <div key={f.stage} data-testid={`funnel-${i}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">{f.stage}</span>
                  <span className="flex items-center gap-2"><span className="font-mono text-slate-200">{fmt(f.value)}</span>{drop !== null && <span className="text-xs text-slate-500">{drop}%</span>}</span>
                </div>
                <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-slate-800">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ delay: i * 0.08, duration: 0.6 }} className="h-full rounded-full bg-gradient-to-r from-teal-500 to-sky-400" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
